import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Save, Bot, Send, Trash2, MessageSquare, X, BookOpen } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const TABS = [
  { id: 'settings', label: 'Genel Ayarlar' },
  { id: 'bot', label: 'Bot Ayarları' },
  { id: 'channels', label: 'Kanal Bağlantıları' },
  { id: 'test', label: 'Bot Testi' },
  { id: 'prompt', label: 'Sistem Promptu' },
  { id: 'conversations', label: 'Konuşmalar' },
  { id: 'reservations', label: 'Rezervasyonlar' },
];

export default function SuperAdminHotelDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [hotel, setHotel] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [activeTab, setActiveTab] = useState('settings');
  const [saving, setSaving] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [promptData, setPromptData] = useState(null);
  const [promptLoading, setPromptLoading] = useState(false);
  const [selectedConv, setSelectedConv] = useState(null);
  const [convMessages, setConvMessages] = useState([]);
  const [convLoading, setConvLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    api.get(`/superadmin/hotels/${id}`).then(setHotel).catch(() => toast.error('Otel yüklenemedi'));
  }, [id]);

  useEffect(() => {
    if (activeTab === 'conversations') {
      api.get(`/superadmin/hotels/${id}/conversations`).then(d => setConversations(d.conversations));
    }
    if (activeTab === 'reservations') {
      api.get(`/superadmin/hotels/${id}/reservations`).then(d => setReservations(d.reservations));
    }
    if (activeTab === 'prompt') {
      setPromptLoading(true);
      api.get(`/superadmin/hotels/${id}/bot-prompt`)
        .then(setPromptData)
        .catch(() => toast.error('Prompt yüklenemedi'))
        .finally(() => setPromptLoading(false));
    }
  }, [activeTab, id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/superadmin/hotels/${id}`, hotel);
      toast.success('Kaydedildi');
    } catch { toast.error('Kaydedilemedi'); }
    setSaving(false);
  };

  const sendTest = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const text = input.trim();
    setInput('');
    setSending(true);
    setMessages(prev => [...prev, { id: Date.now(), role: 'USER', content: text }]);
    try {
      const data = await api.post(`/superadmin/hotels/${id}/bot-test`, { message: text });
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'ASSISTANT', content: data.reply }]);
    } catch { toast.error('Bot yanıt veremedi'); }
    setSending(false);
  };

  const resetTest = async () => {
    await api.delete(`/superadmin/hotels/${id}/bot-test`);
    setMessages([]);
    toast.success('Test sıfırlandı');
  };

  const openConversation = async (conv) => {
    setSelectedConv(conv);
    setConvLoading(true);
    try {
      const data = await api.get(`/superadmin/conversations/${conv.id}`);
      setConvMessages(data.messages || []);
    } catch { toast.error('Mesajlar yüklenemedi'); }
    setConvLoading(false);
  };

  const statusColor = { PENDING: '#d97706', CONFIRMED: '#16a34a', REJECTED: '#dc2626', CANCELLED: '#64748b' };

  if (!hotel) return <div style={{ padding: 28, color: '#94a3b8' }}>Yükleniyor...</div>;

  return (
    <div style={{ padding: 28, position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={() => navigate('/superadmin/hotels')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex' }}>
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>{hotel.name}</h1>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>{hotel.slug} · {hotel._count.conversations} konuşma · {hotel._count.reservations} rezervasyon</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 12, padding: '4px 10px', borderRadius: 20, background: hotel.isActive ? '#dcfce7' : '#fee2e2', color: hotel.isActive ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
            {hotel.isActive ? 'Aktif' : 'Pasif'}
          </span>
          <span style={{ fontSize: 12, padding: '4px 10px', borderRadius: 20, background: '#dbeafe', color: '#1d4ed8', fontWeight: 600 }}>{hotel.plan}</span>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid #e2e8f0', overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500,
            color: activeTab === t.id ? '#3b82f6' : '#64748b', whiteSpace: 'nowrap',
            borderBottom: activeTab === t.id ? '2px solid #3b82f6' : '2px solid transparent',
            marginBottom: -2,
          }}>{t.label}</button>
        ))}
      </div>

      {/* Genel Ayarlar */}
      {activeTab === 'settings' && (
        <div className="card">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div><label>Otel Adı</label><input value={hotel.name || ''} onChange={e => setHotel({ ...hotel, name: e.target.value })} /></div>
            <div><label>Telefon</label><input value={hotel.phone || ''} onChange={e => setHotel({ ...hotel, phone: e.target.value })} /></div>
            <div><label>E-posta</label><input value={hotel.email || ''} onChange={e => setHotel({ ...hotel, email: e.target.value })} /></div>
            <div><label>Şehir</label><input value={hotel.city || ''} onChange={e => setHotel({ ...hotel, city: e.target.value })} /></div>
            <div><label>Plan</label>
              <select value={hotel.plan} onChange={e => setHotel({ ...hotel, plan: e.target.value })}>
                <option value="STARTER">Starter</option>
                <option value="PROFESSIONAL">Professional</option>
                <option value="ENTERPRISE">Enterprise</option>
              </select>
            </div>
            <div><label>Plan Bitiş Tarihi</label>
              <input type="date" value={hotel.planExpiresAt ? hotel.planExpiresAt.split('T')[0] : ''} onChange={e => setHotel({ ...hotel, planExpiresAt: e.target.value || null })} />
            </div>
            <div><label>Durum</label>
              <select value={hotel.isActive ? 'true' : 'false'} onChange={e => setHotel({ ...hotel, isActive: e.target.value === 'true' })}>
                <option value="true">Aktif</option>
                <option value="false">Pasif</option>
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}><label>Adres</label><input value={hotel.address || ''} onChange={e => setHotel({ ...hotel, address: e.target.value })} /></div>
            <div style={{ gridColumn: '1 / -1' }}><label>Açıklama</label><textarea rows={3} value={hotel.description || ''} onChange={e => setHotel({ ...hotel, description: e.target.value })} /></div>
          </div>
          <button className="btn btn-primary" onClick={save} disabled={saving} style={{ marginTop: 16 }}>
            <Save size={15} /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      )}

      {/* Bot Ayarları */}
      {activeTab === 'bot' && (
        <div className="card">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div><label>Konuşma Tonu</label>
              <select value={hotel.botTone || 'FRIENDLY'} onChange={e => setHotel({ ...hotel, botTone: e.target.value })}>
                <option value="PROFESSIONAL">Profesyonel & Resmi</option>
                <option value="FRIENDLY">Samimi & Sıcak</option>
                <option value="ENERGETIC">Enerjik & Modern</option>
              </select>
            </div>
            <div><label>Mesai Başlangıç</label><input type="number" min="0" max="23" value={hotel.workingHoursStart ?? 0} onChange={e => setHotel({ ...hotel, workingHoursStart: parseInt(e.target.value) })} /></div>
            <div><label>Mesai Bitiş</label><input type="number" min="0" max="24" value={hotel.workingHoursEnd ?? 24} onChange={e => setHotel({ ...hotel, workingHoursEnd: parseInt(e.target.value) })} /></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 20 }}>
              <label style={{ margin: 0 }}>Otomatik Devir Al</label>
              <div onClick={() => setHotel({ ...hotel, autoTakeover: !hotel.autoTakeover })} style={{
                width: 44, height: 24, borderRadius: 12, background: hotel.autoTakeover ? '#3b82f6' : '#e2e8f0',
                cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0,
              }}>
                <div style={{
                  position: 'absolute', top: 2, left: hotel.autoTakeover ? 22 : 2,
                  width: 20, height: 20, borderRadius: '50%', background: 'white',
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
            </div>
          </div>
          <div style={{ marginTop: 14 }}>
            <label>Anahtar Kelimeler (otomatik devir için, virgülle ayırın)</label>
            <input
              value={(() => { try { return JSON.parse(hotel.takeoverKeywords || '[]').join(', '); } catch { return ''; } })()}
              onChange={e => setHotel({ ...hotel, takeoverKeywords: JSON.stringify(e.target.value.split(',').map(k => k.trim()).filter(Boolean)) })}
              placeholder="şikayet, sorun, müdür, iptal"
            />
          </div>
          <div style={{ marginTop: 14 }}><label>Karşılama Mesajı</label>
            <textarea rows={3} value={hotel.welcomeMessage || ''} onChange={e => setHotel({ ...hotel, welcomeMessage: e.target.value })} />
          </div>
          <div style={{ marginTop: 12 }}><label>Mesai Dışı Mesajı</label>
            <textarea rows={2} value={hotel.offHoursMessage || ''} onChange={e => setHotel({ ...hotel, offHoursMessage: e.target.value })} />
          </div>
          <button className="btn btn-primary" onClick={save} disabled={saving} style={{ marginTop: 16 }}>
            <Save size={15} /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      )}

      {/* Kanal Bağlantıları */}
      {activeTab === 'channels' && (
        <div className="card">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div><label>Instagram Page ID</label><input value={hotel.instagramPageId || ''} onChange={e => setHotel({ ...hotel, instagramPageId: e.target.value })} /></div>
            <div><label>Instagram Access Token</label><input type="password" value={hotel.instagramAccessToken || ''} onChange={e => setHotel({ ...hotel, instagramAccessToken: e.target.value })} placeholder="••••••••" /></div>
            <div><label>WhatsApp Phone Number ID</label><input value={hotel.whatsappPhoneId || ''} onChange={e => setHotel({ ...hotel, whatsappPhoneId: e.target.value })} /></div>
            <div><label>WhatsApp Access Token</label><input type="password" value={hotel.whatsappAccessToken || ''} onChange={e => setHotel({ ...hotel, whatsappAccessToken: e.target.value })} placeholder="••••••••" /></div>
          </div>
          <div style={{ marginTop: 16, padding: '12px 14px', background: '#f8fafc', borderRadius: 8, fontSize: 13, color: '#64748b' }}>
            <strong>Webhook URL:</strong> <code style={{ background: '#e2e8f0', padding: '2px 6px', borderRadius: 4 }}>{window.location.origin}/api/webhook/meta</code>
          </div>
          <button className="btn btn-primary" onClick={save} disabled={saving} style={{ marginTop: 16 }}>
            <Save size={15} /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      )}

      {/* Bot Testi */}
      {activeTab === 'test' && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', height: 500, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Bot size={16} color="#f59e0b" />
              <span style={{ fontSize: 14, fontWeight: 600 }}>{hotel.name} Botu Test Ediliyor</span>
            </div>
            <button className="btn btn-secondary" onClick={resetTest} style={{ fontSize: 12, padding: '5px 10px' }}>
              <Trash2 size={13} /> Sıfırla
            </button>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, marginTop: 40 }}>
                <Bot size={36} color="#e2e8f0" style={{ margin: '0 auto 10px' }} />
                <div>Bu otelin botu adına test mesajı gönderin</div>
              </div>
            )}
            {messages.map(msg => (
              <div key={msg.id} style={{ display: 'flex', justifyContent: msg.role === 'USER' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '75%', padding: '9px 13px', borderRadius: msg.role === 'USER' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                  background: msg.role === 'USER' ? '#f59e0b' : '#f1f5f9',
                  color: msg.role === 'USER' ? 'white' : '#1e293b', fontSize: 13, lineHeight: 1.6,
                }}>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                </div>
              </div>
            ))}
            {sending && (
              <div style={{ alignSelf: 'flex-start' }}>
                <div style={{ padding: '9px 13px', background: '#f1f5f9', borderRadius: '12px 12px 12px 4px', fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>Yazıyor...</div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <form onSubmit={sendTest} style={{ padding: '12px 16px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 8 }}>
            <input value={input} onChange={e => setInput(e.target.value)} placeholder="Test mesajı yazın..." style={{ flex: 1 }} disabled={sending} />
            <button type="submit" className="btn btn-primary" disabled={sending || !input.trim()}><Send size={15} /></button>
          </form>
        </div>
      )}

      {/* Sistem Promptu */}
      {activeTab === 'prompt' && (
        <div className="card">
          {promptLoading ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Prompt yükleniyor...</div>
          ) : promptData ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>Claude'a Gönderilen Sistem Promptu</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>Tahmini token sayısı: <strong>{promptData.tokenEstimate?.toLocaleString()}</strong></div>
                </div>
              </div>
              <pre style={{
                background: '#0f172a', color: '#7dd3fc', padding: '16px', borderRadius: 8,
                fontSize: 12, lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                maxHeight: 500, overflow: 'auto', fontFamily: 'monospace',
              }}>
                {promptData.prompt}
              </pre>
            </>
          ) : (
            <div style={{ color: '#94a3b8' }}>Prompt yüklenemedi</div>
          )}
        </div>
      )}

      {/* Konuşmalar */}
      {activeTab === 'conversations' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {conversations.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>
              <MessageSquare size={32} color="#e2e8f0" style={{ margin: '0 auto 10px' }} />
              <div>Henüz konuşma yok</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                  {['Müşteri', 'Kanal', 'Durum', 'Mesajlar', 'Son Aktivite', ''].map(h => (
                    <th key={h} style={{ padding: '10px 16px', fontSize: 12, fontWeight: 600, color: '#64748b', textAlign: 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {conversations.map(c => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                    onClick={() => openConversation(c)}>
                    <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 500 }}>{c.customerName || 'Bilinmiyor'}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#dbeafe', color: '#1d4ed8', fontWeight: 600 }}>{c.channel}</span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: c.status === 'ACTIVE' ? '#dcfce7' : '#f1f5f9', color: c.status === 'ACTIVE' ? '#16a34a' : '#64748b', fontWeight: 600 }}>{c.status}</span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 14, color: '#475569' }}>{c._count.messages}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#94a3b8' }}>{new Date(c.lastMessageAt).toLocaleDateString('tr-TR')}</td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: '#3b82f6' }}>Görüntüle →</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Rezervasyonlar */}
      {activeTab === 'reservations' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {reservations.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>
              <BookOpen size={32} color="#e2e8f0" style={{ margin: '0 auto 10px' }} />
              <div>Henüz rezervasyon yok</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                  {['Misafir', 'Oda', 'Giriş', 'Çıkış', 'Fiyat', 'Durum'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', fontSize: 12, fontWeight: 600, color: '#64748b', textAlign: 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reservations.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{r.customerName}</div>
                      <div style={{ fontSize: 12, color: '#94a3b8' }}>{r.customerPhone || ''}</div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#475569' }}>{r.room?.name || '—'}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#475569' }}>{new Date(r.checkIn).toLocaleDateString('tr-TR')}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#475569' }}>{new Date(r.checkOut).toLocaleDateString('tr-TR')}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#475569' }}>{r.totalPrice ? `₺${r.totalPrice.toLocaleString('tr-TR')}` : '—'}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 20, fontWeight: 600, background: (statusColor[r.status] || '#94a3b8') + '18', color: statusColor[r.status] || '#94a3b8' }}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Konuşma Mesaj Slide Panel */}
      {selectedConv && (
        <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 420, background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 15 }}>{selectedConv.customerName || 'Bilinmiyor'}</div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>{selectedConv.channel} · {selectedConv._count?.messages || convMessages.length} mesaj</div>
            </div>
            <button onClick={() => setSelectedConv(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex' }}>
              <X size={20} />
            </button>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {convLoading ? (
              <div style={{ textAlign: 'center', color: '#94a3b8', paddingTop: 40 }}>Yükleniyor...</div>
            ) : convMessages.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#94a3b8', paddingTop: 40 }}>Mesaj bulunamadı</div>
            ) : convMessages.map(msg => (
              <div key={msg.id} style={{ display: 'flex', justifyContent: msg.role === 'USER' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '80%', padding: '8px 12px',
                  borderRadius: msg.role === 'USER' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                  background: msg.role === 'USER' ? '#3b82f6' : '#f1f5f9',
                  color: msg.role === 'USER' ? 'white' : '#1e293b',
                  fontSize: 13, lineHeight: 1.6,
                }}>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                  <div style={{ fontSize: 10, marginTop: 3, opacity: 0.6, textAlign: 'right' }}>
                    {format(new Date(msg.sentAt), 'HH:mm dd MMM', { locale: tr })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Overlay */}
      {selectedConv && (
        <div onClick={() => setSelectedConv(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 999 }} />
      )}
    </div>
  );
}
